/**
 * Frontend type and data structure definitions.
 */

import {VersionedArtifactConnection} from '../../generated/graphql';

// TODO: This is generic, make more broadly available?
export type Json =
  | string
  | number
  | boolean
  | null
  | {[property: string]: Json}
  | Json[];
export type JsonArray = Json[];
export type JsonObject = Record<string, Json>;

export type ResourceType =
  | 'local-container'
  | 'local-process'
  | 'kubernetes'
  | 'sagemaker'
  | 'vertex'
  | 'unknown'; // Only encountered in UI initial states

export const AgentStatuses = {
  Polling: 'POLLING',
  Running: 'RUNNING',
  Stopping: 'STOPPING',
  Killed: 'KILLED',
} as const;
export type AgentStatus = (typeof AgentStatuses)[keyof typeof AgentStatuses];

export type Agent = {
  id: string;
  name: string;
  agentStatus: AgentStatus;
  createdAt: string;
  heartbeatAt: string | null;
  hostname: string;
  runQueues: string[];
  stopPolling: boolean;
  config: string;
  version: string | null;
};

export type LaunchSpec = {
  entity?: string;
  project?: string;
  queue: string;
  queueEntity?: string;
  overrides?: JsonObject;
  job: string;
  resource?: string;
  resource_args?: JsonObject;
  template_variables?: JsonObject;
};

export type ExternalLink = {
  label: string;
  url: string;
};

export type LaunchDrawerJobRun = {
  runName: string;
  jobId: string;
  jobVersion: string;
};

export type JobInfo = {
  id: string;
  versionStr: string;
  name: string;
  versionedJobName: string;
};

export const METADATA_RUNCONFIG_KEY = '@wandb.config';
export const METADATA_FILES_KEY = 'files';
// To differentiate overrides located in files, the config's key will have the format filename::key
// e.g. "config.yaml::learning_rate"
// Overrides from the wandb config have no filename, e.g. "::batch_size"
export const FILE_KEY_DELIMITER = '::';

export type JobMetadataInputTypes = {
  [METADATA_RUNCONFIG_KEY]?: JsonObject;
  [METADATA_FILES_KEY]?: {[key: string]: JsonObject};
};

export type Property = {
  key: string;
  type: string;
  title: string | null;
  description: string | null;
  enum: any[] | null;
  exclusiveMinimum: number | null;
  exclusiveMaximum: number | null;
  minimum: number | null;
  maximum: number | null;
  properties: JsonObject | null;
  file: string | null;
  format: string | null;
  required?: boolean;
  // Array-specific fields (optional)
  items?: {
    type?: string;
    enum?: any[];
    title?: string;
    description?: string;
    format?: string;
  } | null;
  uniqueItems?: boolean | null;
  minItems?: number | null;
  maxItems?: number | null;
  label?: string;
  placeholder?: string;
};

export type PathState = 'ENTITY' | 'PROJECT' | 'ARTIFACT' | 'ALIAS' | 'INVALID';

export type ParsedPath = {
  entity?: string;
  project?: string;
  artifact?: string;
  alias?: string;
  trailingSep: '/' | ':' | null;
};

export interface ArtifactCollectionEdge {
  node: {
    id: string;
    name: string;
    description: string | null;
    createdAt: string;
    project: {
      id: string;
      name: string;
      entityName: string;
    };
    artifacts: VersionedArtifactConnection;
  };
}
export interface VersionInfo {
  hasLatest: boolean;
  latest: number | null;
  highest: number | null;

  // "latest" will be the default if it exists, but since the
  // user can delete that alias, we fall back to "highest" version,
  // but the user can also delete all versions of a job without deleting
  // the job itself, so it may not be runnable at all.
  runTarget: string | null;
  runTargetNum: number | null;

  runsCount: number;
  mostRecentRun: string | null;
}
